import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { TrendingUp, Target, Users, DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';
import ShareButton, { getOGShareUrl } from '../ShareButton';
import { useOddsFormat } from '../../contexts/OddsFormatContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceStats = ({ serviceId, serviceName = "Tip Service", showShareButton = true }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const statsRef = useRef(null);
  const { convertOdds } = useOddsFormat();

  useEffect(() => {
    fetchStats();
  }, [serviceId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/tipster-services/${serviceId}/statistics`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return null;
  }

  const shareUrl = getOGShareUrl('tip-services', serviceId);

  return (
    <Card ref={statsRef} className="glass-dark border-white/10 mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            Statistics
          </CardTitle>
          {showShareButton && (
            <ShareButton 
              elementRef={statsRef}
              shareUrl={shareUrl}
              title={`${serviceName} - Statistics`}
              description={`Check out ${serviceName} on BetBanky! Win Rate: ${stats?.win_rate}%`}
            />
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-blue-400" />
              <p className="text-gray-400 text-sm">Total Tips</p>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total_tips}</p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <p className="text-gray-400 text-sm">Win Rate</p>
            </div>
            <p className="text-2xl font-bold text-green-400">{stats.win_rate}%</p>
            <p className="text-xs text-gray-500 mt-1">{stats.won_tips}W / {stats.lost_tips}L</p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <p className="text-gray-400 text-sm">Avg Odds</p>
            </div>
            <p className="text-2xl font-bold text-white">{convertOdds(stats.average_odds)}</p>
          </div>

          <div className="bg-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-yellow-400" />
              <p className="text-gray-400 text-sm">Followers</p>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{stats.followers || 0}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded p-3 text-center">
            <Clock className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <p className="text-yellow-400 font-bold">{stats.pending_tips}</p>
            <p className="text-gray-400 text-xs">Pending</p>
          </div>
          <div className="bg-red-900/20 border border-red-500/30 rounded p-3 text-center">
            <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-red-400 font-bold">{stats.lost_tips}</p>
            <p className="text-gray-400 text-xs">Lost</p>
          </div>
          <div className="bg-gray-900/20 border border-gray-500/30 rounded p-3 text-center">
            <XCircle className="w-5 h-5 text-gray-400 mx-auto mb-1" />
            <p className="text-gray-400 font-bold">{stats.void_tips}</p>
            <p className="text-gray-400 text-xs">Void</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceStats;